import config from 'config'
import { useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import ChangeBackgroundPanel from 'shared/components/panels/ChangeBackgroundPanel'
import ChangeColorPanel from 'shared/components/panels/ChangeColorPanel'
import {
  CoverParts,
  CoverSides,
  StyledComponentWithCompound
} from 'shared/types/global'
import Flex from 'shared/ui/Flex'

import CoverBackgrounds from './components/CoverBackgrounds'
import CoverSetup from './components/CoverSetup'
import SpineText from './components/SpineText'
import SwitchTemplate from './components/SwitchTemplate'
import UploadCustomCover from './components/UploadCustomCover'
import SwitchTemplatePanel from './components/panels/SwitchTemplatePanel'
import { useCoverBackgrounds } from './hooks/useCoverBackgrounds'

const Section: StyledComponentWithCompound = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-right: 24px;
  width: 100%;
`

Section.Heading = styled(Flex)`
  flex-direction: column;
  gap: 4px;
`

Section.Title = styled.h3`
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  color: ${({ theme }) => theme.color.base.c9};
`

Section.Description = styled.span`
  font-size: 12px;
  margin-bottom: 8px;
  color: ${({ theme }) => theme.color.base.c8};
`

Section.Content = styled.div``

const CoverSettingsWrapper = styled(Flex)<{
  withCustomCover?: boolean
}>`
  position: relative;
  flex-direction: column;
  gap: 32px;

  &::after {
    content: ${({ withCustomCover }) =>
      withCustomCover ? '""' : 'none'};

    position: absolute;
    width: 100%;
    height: 100%;
    background-color: #fff;
    opacity: 0.8;
  }
`

enum SecondLevelPanels {
  changeBackgroundImage,
  changeBackgroundColor,
  switchTemplate
}

type CoverStartEditingPanelProps = {
  withCustomCover?: boolean
  coverSide: CoverSides
}

const CoverStartEditingPanel: React.FC<
  CoverStartEditingPanelProps
> = ({ withCustomCover = false, coverSide }) => {
  const { formatMessage } = useIntl()

  const [secondLevelPanel, setSecondLevelPanel] =
    useState<SecondLevelPanels | null>(null)
  const [changeBackgroundPart, setChangeBackgroundPart] =
    useState<CoverParts | null>(null)

  const {
    backgroundColors,
    backgroundImageUrls,
    refetchBackgroundImages,
    refetchBackgroundColors,
    handleBackgroundColorChange
  } = useCoverBackgrounds()

  const handleChangeBackgroundImagePanelLeave = () => {
    setSecondLevelPanel(null)
    setChangeBackgroundPart(null)

    refetchBackgroundImages()
  }

  const handleChangeBackgroundColorPanelLeave = () => {
    setSecondLevelPanel(null)
    setChangeBackgroundPart(null)

    refetchBackgroundColors()
  }

  return (
    <Flex direction="column" rowGap={32}>
      <CoverSettingsWrapper withCustomCover={withCustomCover}>
        <Section>
          <CoverBackgrounds
            imageUrls={backgroundImageUrls}
            colors={backgroundColors}
            onImageClick={part => {
              setChangeBackgroundPart(part)
              setSecondLevelPanel(
                SecondLevelPanels.changeBackgroundImage
              )
            }}
            onColorClick={part => {
              setChangeBackgroundPart(part)
              setSecondLevelPanel(
                SecondLevelPanels.changeBackgroundColor
              )
            }}
          />
        </Section>

        <Section>
          <SwitchTemplate
            onButtonClick={() => {
              setSecondLevelPanel(SecondLevelPanels.switchTemplate)
            }}
          />
        </Section>

        {coverSide === CoverSides.outside && (
          <Section>
            <Section.Title>
              {formatMessage({
                id: 'Panel.coverStartEditing.spineText.title'
              })}
            </Section.Title>
            <Section.Content>
              <SpineText />
            </Section.Content>
          </Section>
        )}

        <Section>
          <Section.Heading>
            <Section.Title>
              {formatMessage({
                id: 'Panel.coverStartEditing.coverSetup.title'
              })}
            </Section.Title>
            <Section.Description>
              {formatMessage({
                id: 'Panel.coverStartEditing.coverSetup.description'
              })}
            </Section.Description>
          </Section.Heading>
          <Section.Content>
            <CoverSetup />
          </Section.Content>
        </Section>
      </CoverSettingsWrapper>

      <Section>
        <Section.Heading>
          <Section.Title>
            {formatMessage({
              id: 'Panel.coverStartEditing.uploadOwnCover.title'
            })}
          </Section.Title>
          <Section.Description>
            {formatMessage({
              id: 'Panel.coverStartEditing.uploadOwnCover.description'
            })}
          </Section.Description>
        </Section.Heading>

        <Section.Content>
          <UploadCustomCover />
        </Section.Content>
      </Section>

      {secondLevelPanel ===
        SecondLevelPanels.changeBackgroundImage && (
        <ChangeBackgroundPanel
          variableName={
            config.themes.variablesKeys.cover.background.image[
              changeBackgroundPart as CoverParts
            ]
          }
          onBackButtonClick={() =>
            handleChangeBackgroundImagePanelLeave()
          }
          withResetButton
          showDeleteBackgroundButton
        />
      )}

      {secondLevelPanel ===
        SecondLevelPanels.changeBackgroundColor && (
        <ChangeColorPanel
          titleId="Panel.title.changeBackgroundColor"
          value={
            backgroundColors[changeBackgroundPart as CoverParts] || {
              c: 0,
              m: 0,
              y: 0,
              k: 0
            }
          }
          colorName="background"
          variableName={
            config.themes.variablesKeys.cover.background.image[
              changeBackgroundPart as CoverParts
            ]
          }
          onBackButtonClick={handleChangeBackgroundColorPanelLeave}
          onChange={newColor =>
            handleBackgroundColorChange(
              changeBackgroundPart as CoverParts,
              newColor
            )
          }
        />
      )}

      {secondLevelPanel === SecondLevelPanels.switchTemplate && (
        <SwitchTemplatePanel
          onBackButtonClick={() => {
            setSecondLevelPanel(null)
          }}
        />
      )}
    </Flex>
  )
}

export default CoverStartEditingPanel
